/**
 * External subscribers — the people imported from CSV who are not CRM
 * contacts. Search, edit, delete; a suppressed address carries a badge because
 * it stays on lists but is skipped at send time.
 *
 * Edits are sent as a DIFF against the record that was opened: an untouched
 * field sends nothing, and a cleared one sends `null`.
 */
import { useEffect, useState } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  Badge,
  Button,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Panel,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { api, ApiError } from '@/lib/api';
import { applyNameEdit, composeFullName, CONTACT_NAME_LABELS, type ContactNameParts } from '@/lib/contact-name';
import { useAuth } from '@/lib/use-auth';
import type { MarketingSubscriber, MarketingSubscriberInput } from '@/lib/types';
import { Pager } from './ListMembersTable';
import { useDebounced } from './ref-options';

const PER_PAGE = 25;

function fullName(s: MarketingSubscriber): string {
  return composeFullName(s.firstName, s.lastName);
}

export function SubscribersTable() {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('marketing-lists');
  const canDelete = ability.canDelete('marketing-lists');

  const [search, setSearch] = useRememberedState('subscribers-search', '');
  const debounced = useDebounced(search.trim());
  const [page, setPage] = useState(1);
  const [lastSearch, setLastSearch] = useState(debounced);
  if (lastSearch !== debounced) {
    setLastSearch(debounced);
    setPage(1);
  }

  const [rows, setRows] = useState<MarketingSubscriber[] | null>(null);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [editing, setEditing] = useState<MarketingSubscriber | null>(null);
  const [deleting, setDeleting] = useState<MarketingSubscriber | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listMarketingSubscribers(
        { page, perPage: PER_PAGE, ...(debounced ? { search: debounced } : {}) },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setRows(response.data);
        setMeta({ total: response.meta.total, totalPages: response.meta.totalPages });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRows(null);
        setError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'Subscribers could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, debounced, reload]);

  const confirmDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteMarketingSubscriber(deleting.id);
      setDeleting(null);
      setReload((n) => n + 1);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'The subscriber could not be deleted.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const showActions = canUpdate || canDelete;

  return (
    <Panel>
      <PanelHeader title="Subscribers" />
      <div className="border-t border-border px-4 py-3">
        <Input
          type="search"
          aria-label="Search subscribers"
          placeholder="Search by name, email or company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-sm"
        />
      </div>

      {error ? (
        <div className="px-4 pb-4">
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        </div>
      ) : loading && rows === null ? (
        <div className="grid gap-2 px-4 pb-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          title={debounced ? 'No subscribers match' : 'No subscribers yet'}
          description={debounced ? 'Try a different search.' : 'Import a CSV into a static list to add subscribers.'}
        />
      ) : (
        <TableWrap frozenHeader>
          <Table className="min-w-[760px]">
            <THead>
              <TR>
                <TH>Email</TH>
                <TH>Name</TH>
                <TH>Company</TH>
                <TH>Country</TH>
                <TH>Source</TH>
                <TH numeric>Lists</TH>
                {showActions && <TH className="w-[150px]">Actions</TH>}
              </TR>
            </THead>
            <TBody>
              {rows.map((s) => (
                <TR key={s.id}>
                  <TD className="max-w-[18rem]">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate" title={s.email}>
                        <EmailLink address={s.email} />
                      </span>
                      {s.suppressed && <Badge tone="danger">Suppressed</Badge>}
                    </div>
                  </TD>
                  <TD className="max-w-[12rem] truncate">{fullName(s) || '—'}</TD>
                  <TD className="max-w-[12rem] truncate" title={s.companyName ?? undefined}>
                    {s.companyName ?? '—'}
                  </TD>
                  <TD>{s.country?.name ?? '—'}</TD>
                  <TD className="max-w-[10rem] truncate" title={s.source ?? undefined}>
                    {s.source ?? '—'}
                  </TD>
                  <TD numeric>{s.listCount}</TD>
                  {showActions && (
                    <TD>
                      <div className="flex gap-1.5">
                        {canUpdate && (
                          <Button variant="secondary" size="sm" onClick={() => setEditing(s)}>
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeleteError(null);
                              setDeleting(s);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </TD>
                  )}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      )}

      {rows && (
        <Pager
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          shown={rows.length}
          noun={{ singular: 'subscriber', plural: 'subscribers' }}
          onPage={setPage}
        />
      )}

      {editing && (
        <SubscriberEditModal
          subscriber={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setReload((n) => n + 1);
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => (deleteBusy ? undefined : setDeleting(null))}
        onConfirm={confirmDelete}
        title="Delete this subscriber?"
        description={
          deleting
            ? `${deleting.email} will be deleted${
                deleting.listCount === 0
                  ? ''
                  : ` and removed from ${deleting.listCount === 1 ? 'the 1 list' : `all ${deleting.listCount} lists`} it is on`
              }, and can't be recovered. A suppression on this address is not removed.`
            : ''
        }
        confirmLabel="Delete subscriber"
        loading={deleteBusy}
        error={deleteError}
      />
    </Panel>
  );
}

const EDIT_KEYS = ['email', 'firstName', 'lastName', 'companyName', 'countryId', 'source'] as const;

function SubscriberEditModal({
  subscriber,
  onClose,
  onSaved,
}: {
  subscriber: MarketingSubscriber;
  onClose(): void;
  onSaved(): void;
}) {
  const [email, setEmail] = useState(subscriber.email);
  // Subscribers store only first + last; Full name is a client-side field kept
  // in step both ways by `applyNameEdit` and never sent (the parts are).
  const [names, setNames] = useState<ContactNameParts>(() => ({
    firstName: subscriber.firstName ?? '',
    lastName: subscriber.lastName ?? '',
    name: composeFullName(subscriber.firstName, subscriber.lastName),
  }));
  const { firstName, lastName } = names;
  const editName = (field: keyof ContactNameParts, value: string) =>
    setNames((current) => applyNameEdit(current, field, value));
  const [companyName, setCompanyName] = useState(subscriber.companyName ?? '');
  const [countryId, setCountryId] = useState(subscriber.country ? String(subscriber.country.id) : '');
  const [source, setSource] = useState(subscriber.source ?? '');
  const [countries, setCountries] = useState<{ value: string; label: string }[] | null>(null);
  const [countriesFailed, setCountriesFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCountries({ perPage: 250 }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setCountries(response.data.map((c) => ({ value: String(c.id), label: c.countryName })));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCountriesFailed(true);
      });
    return () => controller.abort();
  }, []);

  const countryOptions = (() => {
    const list = countries ?? [];
    if (subscriber.country && !list.some((c) => c.value === String(subscriber.country?.id))) {
      return [{ value: String(subscriber.country.id), label: subscriber.country.name }, ...list];
    }
    return list;
  })();

  const patch: MarketingSubscriberInput = {};
  const norm = (v: string) => (v.trim() === '' ? null : v.trim());
  if (email.trim().toLowerCase() !== subscriber.email) patch.email = email.trim();
  if (norm(firstName) !== subscriber.firstName) patch.firstName = norm(firstName);
  if (norm(lastName) !== subscriber.lastName) patch.lastName = norm(lastName);
  if (norm(companyName) !== subscriber.companyName) patch.companyName = norm(companyName);
  if ((countryId ? Number(countryId) : null) !== (subscriber.country?.id ?? null)) {
    patch.countryId = countryId ? Number(countryId) : null;
  }
  if (norm(source) !== subscriber.source) patch.source = norm(source);
  const dirty = Object.keys(patch).length > 0;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (saving || !dirty) return;
    if (patch.email !== undefined && !patch.email) {
      setFieldErrors({ email: 'A subscriber needs an email address.' });
      return;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await api.updateMarketingSubscriber(subscriber.id, patch);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const { fields, unroutable } = err.routeFieldErrors(EDIT_KEYS);
        setFieldErrors(fields);
        setFormError(
          unroutable.length > 0
            ? unroutable.join(' ')
            : Object.keys(fields).length > 0
              ? null
              : (err.formMessage ?? err.message),
        );
      } else {
        setFormError('The subscriber could not be saved.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (saving ? undefined : onClose())}
      title="Edit subscriber"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="marketing-subscriber-edit" loading={saving} disabled={!dirty}>
            Save changes
          </Button>
        </div>
      }
    >
      <form id="marketing-subscriber-edit" onSubmit={save} className="grid gap-4">
        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}
        <Field label="Email" required error={fieldErrors.email}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              maxLength={320}
              onChange={(e) => setEmail(e.target.value)}
              invalid={Boolean(fieldErrors.email)}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={CONTACT_NAME_LABELS.firstName} error={fieldErrors.firstName}>
            {(props) => (
              <Input
                {...props}
                value={firstName}
                maxLength={120}
                onChange={(e) => editName('firstName', e.target.value)}
              />
            )}
          </Field>
          <Field label={CONTACT_NAME_LABELS.lastName} error={fieldErrors.lastName}>
            {(props) => (
              <Input {...props} value={lastName} maxLength={120} onChange={(e) => editName('lastName', e.target.value)} />
            )}
          </Field>
        </div>
        <Field label={CONTACT_NAME_LABELS.name}>
          {(props) => (
            <Input {...props} value={names.name} maxLength={241} onChange={(e) => editName('name', e.target.value)} />
          )}
        </Field>
        <Field label="Company" error={fieldErrors.companyName}>
          {(props) => (
            <Input {...props} value={companyName} maxLength={255} onChange={(e) => setCompanyName(e.target.value)} />
          )}
        </Field>
        <Field
          label="Country"
          error={fieldErrors.countryId ?? (countriesFailed ? 'The country list could not be loaded.' : undefined)}
        >
          {(props) => (
            <Combobox
              id={props.id}
              aria-describedby={props['aria-describedby']}
              value={countryId}
              onChange={setCountryId}
              options={countryOptions}
              emptyOptionLabel="No country"
              loading={countries === null && !countriesFailed}
              disabled={countriesFailed}
            />
          )}
        </Field>
        <Field label="Source" error={fieldErrors.source}>
          {(props) => <Input {...props} value={source} maxLength={120} onChange={(e) => setSource(e.target.value)} />}
        </Field>
      </form>
    </Modal>
  );
}
